/**
 * Fireworks — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fireworksPack: ToilePack = {
    slug: 'fireworks',
    category: 'motif',
    name: 'Fireworks',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'burst', label: 'Single burst', subject: 'A single huge chrysanthemum firework bursting in gold and red against a black sky, trails curving down, a few sparks falling, nothing else.'},
        {key: 'city', label: 'Over the city', subject: "A display of overlapping bursts in pink, blue, gold and green over a city skyline at night, a bridge, a crowd's silhouettes on a waterfront, smoke drifting."},
        {key: 'water', label: 'Over the water', subject: 'Fireworks bursting over a calm bay with their reflections doubled in the water, a few small boats watching, a pier with lanterns, a dark hill.'},
    ],
    styles: [
        {key: 'poster', label: 'Flat poster', style: 'flat poster illustration, limited palette on black, clean shapes, print texture', palette: 'a limited poster palette of gold, red and blue on black', framing: 'centred on a plain black background with a thin printed border'},
        {key: 'painted', label: 'Painted print', style: 'painted print, luminous colour, deep night sky, textured canvas', palette: 'luminous gold, pink and blue against a deep black night', framing: 'set on a deep night-sky background, the light glowing outward'},
        {key: 'line', label: 'Line drawing', style: 'single-weight white line drawing on black, clean contour', palette: 'white ink only, no other colour', framing: 'centred on a plain black background with a wide even margin'},
    ],
    directions: ['a New Year countdown', 'in red and gold only', 'for a phone wallpaper'],
    directionHint: 'try: a New Year countdown',
    seo: {
        title: 'Fireworks art: single burst, city, over water',
        metaDescription: 'Nine fireworks illustrations — a single burst, over a city and over water — in flat poster, painted and line-drawing styles for any celebration.',
        h1: 'Fireworks drawings: single burst, city skyline, over water in three styles',
        tagline: 'Nine renders, three firework readings, three looks each — poster to line.',
        intro: "The burst in the sky drawn as a design problem, not a photograph: a single chrysanthemum burst, a display over a city skyline, and fireworks doubled in a bay's reflection. Each renders as a flat poster, a painted print and a white line on black. Ask for a New Year countdown or red-and-gold only and the same three looks carry it.",
        useCases: [
            'a New Year or holiday card',
            'a phone wallpaper',
            'a festival or event poster',
            'a social media celebration post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a tall portrait crop, suited to a poster, a card or a phone wallpaper.'},
            {q: 'Can I get a New Year countdown or a different colour scheme?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Flat poster holds a limited palette on black, painted print is luminous and textured, and line drawing is white ink only on a black ground.'},
            {q: 'Is this a real photograph of fireworks?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
